import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	Switch,
	TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_FORMAT } from '@/utils/dateHelper';
import { ReportListItem } from '../ReportListSummaryPage';
import { useDownloadReport } from '../useDownloadReport';

interface DownloadReportForm {
	start: string | null;
	end: string | null;
	series: string;
	hasOnline: boolean;
}

const emptyValues: DownloadReportForm = {
	start: null,
	end: null,
	series: '',
	hasOnline: true,
};

interface Props {
	report: ReportListItem | null;
	open: boolean;
	onClose: () => void;
	// When the report is scoped to a fixed series (e.g. from a series detail
	// page), the series is supplied here instead of being entered by the user.
	fixedSeries?: { id: string; title?: string };
}

export default function DownloadReportDialog({
	report,
	open,
	onClose,
	fixedSeries,
}: Props) {
	const { t } = useTranslation();
	const { download, isLoading } = useDownloadReport();

	const methods = useForm<DownloadReportForm>({ defaultValues: emptyValues });
	const watchedStart = methods.watch('start');

	useEffect(() => {
		if (!open || !report) return;
		const hasDate =
			report.filters.includes('start') || report.filters.includes('end');
		methods.reset({
			...emptyValues,
			// default to the last 365 days for date-scoped reports
			start: hasDate
				? dayjs().subtract(365, 'day').format(API_DATE_FORMAT)
				: null,
			end: hasDate ? dayjs().format(API_DATE_FORMAT) : null,
		});
	}, [open]);

	if (report == null) return null;

	const filters = report.filters;
	const showDate = filters.includes('start') || filters.includes('end');
	const showHasOnline = filters.includes('hasOnline');

	const startLabel =
		report.dateScope === 'register'
			? t('reportList.registerStartDate')
			: t('reportList.periodStartDate');
	const endLabel =
		report.dateScope === 'register'
			? t('reportList.registerEndDate')
			: t('reportList.periodEndDate');

	const onSubmit = async (values: DownloadReportForm) => {
		const ok = await download(report, {
			...values,
			series: fixedSeries ? fixedSeries.id : values.series,
		});
		if (ok) onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
			<DialogTitle variant='h5'>{t('reportList.downloadTitle')}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<FormProvider {...methods}>
					<Grid container spacing={3} marginTop={0}>
						<Grid item xs={12}>
							<TextField
								label={t('reportList.reportName')}
								value={t(report.nameKey)}
								variant='filled'
								fullWidth
								InputProps={{ readOnly: true }}
							/>
						</Grid>
						{fixedSeries && (
							<Grid item xs={12}>
								<TextField
									label={t('reportList.seriesTitle')}
									value={fixedSeries.title ?? '-'}
									variant='filled'
									fullWidth
									InputProps={{ readOnly: true }}
								/>
							</Grid>
						)}
						{showDate && (
							<>
								<Grid item xs={6}>
									<AppDatePicker
										control={methods.control}
										name='start'
										label={startLabel}
										rules={{ required: t('errorMessage.required') }}
									/>
								</Grid>
								<Grid item xs={6}>
									<AppDatePicker
										control={methods.control}
										name='end'
										label={endLabel}
										minimumDate={watchedStart ?? undefined}
										rules={{ required: t('errorMessage.required') }}
									/>
								</Grid>
							</>
						)}
						{showHasOnline && (
							<Grid item xs={12}>
								<Controller
									control={methods.control}
									name='hasOnline'
									render={({ field }) => (
										<FormControlLabel
											control={
												<Switch
													checked={field.value}
													onChange={(e) => field.onChange(e.target.checked)}
												/>
											}
											label={t('reportList.hasOnline')}
										/>
									)}
								/>
							</Grid>
						)}
					</Grid>
				</FormProvider>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t('button.cancel')}</Button>
				<Button
					variant='contained'
					disabled={isLoading}
					onClick={methods.handleSubmit(onSubmit)}
				>
					{t('reportList.download')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
